import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getLinkedOrgs, linkedOrgsLoading } from '../selectors/OrgManagementSelectors';
import css from '../../../css/css-variables.json';
import DismissIcon from '../../../img/sprite/x-icon.svg';
import TadaIcon from '../../../img/sprite/tada.svg';
import RepeatIcon from '../../../img/sprite/repeat-icon-dark.svg';
import { usePendingInvitations } from '../hooks/invitation';
import useOnClickOutside from '../hooks/useOnClickOutside';

const Button = styled.button`
  display: flex;
  justify-content: space-between;
  align-content: center;
  font-size: 12px;
  position: relative;
  color: ${css.colors.Primary};
  background: none;
  border: none;
  padding: 0;
  outline: 0;
  text-decoration: underline;
`;

const Wrapper = styled.div`
  background-color: ${css.colors.Form};
  padding: 20px;
  position: absolute;
  z-index: 1000;
  top: 70px;
  right: 100px;
  width: 400px;
  border-radius: 5px;
  color: ${css.colors.ButtonBlue};

  :before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-width: 10px;
    border-style: solid;
    border-color: transparent transparent ${css.colors.Notification} transparent;
    top: -20px;
    left: 53%;
  }

  @media only screen and (max-width: 720px) {
    width: 100%;
    left: 0;
    top: 42px;
  }
`;

const InfoBlock = styled.div`
  background-color: ${css.colors.AccentLight};
  border-radius: 2px;
  margin: 10px;
  border-radius: 5px;
  padding: 10px;
`;

const Text = styled.p`
  margin: 5px;
`;

const DismissButton = styled.button`
  float: right;
  border: 0 none;
  background-color: transparent;

  display: flex;

  margin-top: -0.125em;
  margin-right: -0.5em;
  padding: .5em;
  border-radius: 50%;
  transition: all .25s ease;

  &:hover {
    background-color: rgba(34, 34, 34, .15);
  }

  & svg {
    width: 10px;
    height: 10px;
  }
`;

const EmptyContainer = styled.div`
  /* fill space of What's New? text when it is not rendered */
  width: 69px;
`;

const NotificationBadge = styled.span`
  position: absolute;
  right: -10px;
  top: -15px;
  min-width: 20px;
  min-height: 10px;
  line-height: 8px;
  padding: 5px;
  background-color:${css.colors.Delete};
  color: ${css.colors.Background};
  font-size: 10px;
  border-radius: 20px;
`;

const TadaImg = styled.svg`
  height: 60px;
  width: 60px;
`;

const TTHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TTHeaderContent = styled.p`
  margin: 0 0 0 12px;
  color: ${css.colors.Body};
  line-height: 1.1;
`;

const TTContentWrapper = styled.div`
  padding: 8px 12px;
  background-color: ${css.colors.Background};
  border-radius: 2.5px;
  margin-bottom: 16px;
  `;

const TTBodyTitle = styled.p`
  color: ${css.colors.Body};
  font-size: 0.875rem;
  margin: 0;
  `;

const TTBodyContent = styled.p`
  font-size: 0.875rem;
  margin: 4px 0 0 0;
  `;

const TTBodyTitleContainer = styled.div`
  display: flex;
  `;

const TTFooter = styled.p`
  font-size: 12px;
  margin: 0;
  text-align: center;
  `;

const HR = styled.hr`
  color: #bbc1c8;
  margin: 16px -4px;
`;

const RepeatImg = styled.svg`
  margin-left: 12px;
`;

const NewFeaturesToolTip = () => {
  const [showNewFeaturesToolTip, setShowNewFeaturesToolTip] = useState(false);
  const [showNewFeaturesBadge, setShowNewFeaturesBadge] = useState(false);

  const closeOnClick = () => setShowNewFeaturesToolTip(false);

  const ref = useRef();
  useOnClickOutside(ref, () => setShowNewFeaturesToolTip(false));

  const closeToolTip = () => {
    setShowNewFeaturesToolTip(false);
    setShowNewFeaturesBadge(false);
  };

  const toggleToolTip = (showNewFeaturesToolTip) => {
    setShowNewFeaturesToolTip(!showNewFeaturesToolTip);
    setShowNewFeaturesBadge(false);
  };

  const linkedOrgs = useSelector(getLinkedOrgs);
  const loadingOrgs = useSelector(linkedOrgsLoading);
  const isFirstRun = useRef(true);
  const { invitations } = usePendingInvitations();

  useEffect(() => {
    if (invitations.length === 0 && linkedOrgs.length > 0 && loadingOrgs === 'success' && isFirstRun.current === true && !localStorage.getItem('hideWhatsNew')) {
      localStorage.setItem('hideWhatsNew', true);
      isFirstRun.current = false;
      setShowNewFeaturesToolTip(true);
      setShowNewFeaturesBadge(true);
    } else if (invitations.length > 0 && linkedOrgs.length > 0 && loadingOrgs === 'success' && isFirstRun.current === true && !localStorage.getItem('hideWhatsNew')) {
      localStorage.setItem('hideWhatsNew', true);
      isFirstRun.current = false;
      setShowNewFeaturesBadge(true);
    }
  }, [linkedOrgs, loadingOrgs, invitations.length]);

  if (loadingOrgs === 'success') {
    return (
      <div ref={ref}>
        <Button onClick={() => toggleToolTip(showNewFeaturesToolTip)}>
          What&apos;s New?
          {showNewFeaturesBadge ? <NotificationBadge>4</NotificationBadge> : null}
        </Button>
        {showNewFeaturesToolTip && linkedOrgs.length > 0 &&
          <Wrapper>
            <DismissButton onClick={() => closeToolTip()} aria-label="Dismiss Updates">
              <svg role="img"><use href={`#${DismissIcon.id}`} /></svg>
            </DismissButton>
            <TTHeaderWrapper>
              <TadaImg role="img"><use href={`#${TadaIcon.id}`} /></TadaImg>
              <TTHeaderContent>You can do more with organization management! Here&apos;s what&apos;s new!</TTHeaderContent>
            </TTHeaderWrapper>
            <HR />
            <TTContentWrapper>
              <TTBodyTitleContainer>
                <TTBodyTitle>Switch Profiles</TTBodyTitle>
                <RepeatImg role="img"><use href={`#${RepeatIcon.id}`} /></RepeatImg>
              </TTBodyTitleContainer>
              <TTBodyContent>Switch between your personal account and an organization or church that you&apos;re linked to.</TTBodyContent>
            </TTContentWrapper>
            <TTContentWrapper>
              <TTBodyTitleContainer>
                <TTBodyTitle>Online Bill Pay - Save ACH Info</TTBodyTitle>
              </TTBodyTitleContainer>
              <TTBodyContent>Now you can save and manage bank account info for future payments.</TTBodyContent>
              <TTBodyContent><em>Hint:</em> Create a group to unlock this ability.</TTBodyContent>
            </TTContentWrapper>
            <TTContentWrapper>
              <TTBodyTitleContainer>
                <TTBodyTitle>Invite People to a Group</TTBodyTitle>
              </TTBodyTitleContainer>
              <TTBodyContent>Add people to an administrator or billing manager group on the Groups page.</TTBodyContent>
              <TTBodyContent><em>Hint:</em> There must be at least one Org Admin to unlock this ability.</TTBodyContent>
            </TTContentWrapper>
            <TTContentWrapper>
              <TTBodyTitleContainer>
                <TTBodyTitle>View Organization Profile</TTBodyTitle>
              </TTBodyTitleContainer>
              <TTBodyContent>View org information at-a-glance, including billing accounts on the Organization Profile page.</TTBodyContent>
            </TTContentWrapper>
            <TTFooter>
              <a
                href="https://www.lifeway.com/en/about/organization-management"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read more{' '}
              </a>
              about the new Org. Management features.
            </TTFooter>
          </Wrapper>
        }
        {showNewFeaturesToolTip && linkedOrgs.length === 0 &&
          <Wrapper>
            <DismissButton onClick={() => setShowNewFeaturesToolTip(false)} aria-label="Dismiss Updates">
              <svg role="img"><use href={`#${DismissIcon.id}`} /></svg>
            </DismissButton>
            <Text>
              We&apos;ve made some changes to improve your experience.
              Here&apos;s what&apos;s new!
            </Text>
            <InfoBlock>
              <Text>For large simulcast licenses you can share a redemption code to let attendees redeem a code for access. Go to <Link onClick={closeOnClick} to="/manage-access/simulcasts">Manage Access</Link> to get your code.</Text>
            </InfoBlock>
            <InfoBlock>
              <Text>Updated menu navigation.</Text>
              <Text><i>Hint:</i> You’ll find <Link onClick={closeOnClick} to="/payment-methods/gift-cards">Gift Cards</Link> under Payment Methods.</Text>
              <Text><i>Hint:</i> <Link  onClick={closeOnClick} to="/order-history">Orders</Link> and <Link onClick={closeOnClick} to="/digital-media">Digital Media</Link> are now in the main navigation.</Text>
            </InfoBlock>
            <InfoBlock>
              <Text>Pay your LifeWay billing account online using a bank account. Go to Account Activity to pay now.</Text>
            </InfoBlock>
          </Wrapper>
        }
      </div>
    );
  } else {
    return <EmptyContainer></EmptyContainer>;
  }

};

export default NewFeaturesToolTip;
